/**
 * member-channel-access-view.tsx — Tab "Phân quyền theo kênh" trong Quản lý nhân viên, đi từ NHÂN VIÊN:
 * chọn một nhân viên (cột trái) → xem / tick các tài khoản kênh người đó được vào (cột phải).
 *
 * Cùng bảng `ChannelAccountAccess` với cột "Kênh phụ trách" và hộp "Tài khoản phụ trách"
 * (`MemberAccountsPanel` dùng chung). Có công tắc phụ "Theo kênh" để xem chiều ngược lại
 * (từng tài khoản đang giao cho ai — `PermissionsSection` cũ).
 */
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Check, Loader2, Search, ShieldCheck, Wifi, WifiOff } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/misc'
import { Loading, EmptyState } from '@/components/shared/feedback'
import { useTeam, useGrantZaloAccess, useRevokeZaloAccess, type TeamMember } from '@/hooks/use-settings'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { apiError } from '@/lib/api-client'
import { PLATFORM_LABEL, ROLE_LABELS, avatarSrc } from '@/pages/settings/settings-utils'
import { cn, initials } from '@/lib/utils'
import { AccessLevelLegend, AccessLevelSelect } from '@/components/shared/access-level-select'
import { DEFAULT_ACCESS_LEVEL, needsExplicitAccess, type AccessLevel } from '@/lib/channel-access'
import { PermissionsSection } from './permissions-section'


const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase()

/** Danh sách tài khoản kênh có ô tick cho MỘT nhân viên — dùng chung cho tab và hộp thoại. */
export function MemberAccountsPanel({ member, className }: { member: TeamMember; className?: string }) {
  const { t } = useTranslation('team')
  const { data: accounts, isLoading } = useZaloAccounts(undefined, true)
  const { data: team } = useTeam()
  const grant = useGrantZaloAccess()
  const revoke = useRevokeZaloAccess()
  const [busy, setBusy] = useState<string | null>(null)

  // Đọc bản mới nhất từ query (prop là ảnh chụp lúc chọn).
  const fresh = team?.find((m) => m.id === member.id) ?? member
  const assigned = new Map((fresh.accounts ?? []).map((a) => [a.id, a.level ?? 'manage'] as const))

  const toggle = async (accountId: string, name: string, checked: boolean) => {
    setBusy(accountId)
    try {
      if (checked) await grant.mutateAsync({ accountId, userId: member.id, level: DEFAULT_ACCESS_LEVEL })
      else await revoke.mutateAsync({ accountId, userId: member.id })
      toast.success(checked ? t('memberChannelAccessView.granted', { name }) : t('memberChannelAccessView.revoked', { name }))
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setBusy(null)
    }
  }

  const changeLevel = async (accountId: string, name: string, level: AccessLevel) => {
    setBusy(accountId)
    try {
      await grant.mutateAsync({ accountId, userId: member.id, level })
      toast.success(`${name}: ${level === 'agent' ? t('memberChannelAccessView.agentLevel') : t('memberChannelAccessView.manageLevel')}`)
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setBusy(null)
    }
  }

  const byPlatform = new Map<number, NonNullable<typeof accounts>>()
  for (const a of accounts ?? []) {
    const list = byPlatform.get(a.platform) ?? []
    list.push(a)
    byPlatform.set(a.platform, list)
  }

  if (!needsExplicitAccess(member)) {
    return (
      <div className={cn('rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground', className)}>
        <ShieldCheck className="mx-auto mb-1.5 h-5 w-5 text-success" />
        {member.roleName || ROLE_LABELS[member.role] || member.role} <strong>{t('memberChannelAccessView.alwaysAllAccounts')}</strong> {t('memberChannelAccessView.noNeedToAssign')}
      </div>
    )
  }
  if (isLoading) return <Loading label={t('memberChannelAccessView.loadingAccounts')} />
  if (!accounts?.length) {
    return (
      <EmptyState
        icon={Wifi}
        title={t('memberChannelAccessView.noAccounts')}
        description={t('memberChannelAccessView.noAccountsHint')}
      />
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="text-xs text-muted-foreground">
        <Trans
          t={t}
          i18nKey="memberChannelAccessView.assignedSummary"
          values={{ assigned: assigned.size, total: accounts.length }}
          components={{ b: <Badge variant="secondary" className="mx-0.5" /> }}
        />
      </div>
      <AccessLevelLegend />
      {[...byPlatform.entries()].map(([platform, list]) => (
        <div key={platform}>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
            {PLATFORM_LABEL[platform] ?? t('memberChannelAccessView.unknownPlatform', { platform })}
          </p>
          <ul className="divide-y rounded-lg border">
            {list.map((a) => {
              const checked = assigned.has(a.id)
              const level = assigned.get(a.id) ?? DEFAULT_ACCESS_LEVEL
              const online = (a.liveStatus ?? a.status) === 'connected'
              return (
                <li key={a.id} className="flex items-center gap-3 px-3 py-2.5">
                  <Checkbox
                    checked={checked}
                    disabled={busy === a.id}
                    onCheckedChange={(v) => toggle(a.id, a.displayName || t('memberChannelAccessView.anAccount'), v === true)}
                    aria-label={a.displayName ?? ''}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{a.displayName || t('memberChannelAccessView.unnamed')}</span>
                    <span className={cn('flex items-center gap-1 text-xs', online ? 'text-success' : 'text-muted-foreground')}>
                      {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                      {online ? t('memberChannelAccessView.online') : t('memberChannelAccessView.offline')}
                    </span>
                  </span>
                  {busy === a.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : checked ? (
                    <>
                      <AccessLevelSelect
                        value={level}
                        onChange={(l) => changeLevel(a.id, a.displayName || t('memberChannelAccessView.anAccount'), l)}
                        ariaLabel={t('memberChannelAccessView.accessLevelOf', { name: fresh.fullName || fresh.email, channel: a.displayName ?? t('memberChannelAccessView.aChannel') })}
                      />
                      <Check className="h-4 w-4 text-success" />
                    </>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}

export function MemberChannelAccessView() {
  const { t } = useTranslation('team')
  const { data: team, isLoading } = useTeam()
  const [mode, setMode] = useState<'member' | 'channel'>('member')
  const [q, setQ] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const members = useMemo(() => {
    const list = (team ?? []).filter((m) => m.status === 'active')
    const f = fold(q.trim())
    const shown = f ? list.filter((m) => fold(`${m.fullName} ${m.email}`).includes(f)) : list
    // Nhân viên (cần giao kênh) lên trước, rồi theo tên.
    return [...shown].sort((a, b) =>
      Number(needsExplicitAccess(b)) - Number(needsExplicitAccess(a)) || a.fullName.localeCompare(b.fullName, 'vi'))
  }, [team, q])

  const selected = members.find((m) => m.id === selectedId) ?? members.find((m) => needsExplicitAccess(m)) ?? members[0]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {t('memberChannelAccessView.intro')}
        </p>
        <div className="inline-flex rounded-lg border p-0.5 text-sm">
          {(['member', 'channel'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn('rounded-md px-3 py-1', mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
            >
              {m === 'member' ? t('memberChannelAccessView.byMember') : t('memberChannelAccessView.byChannel')}
            </button>
          ))}
        </div>
      </div>

      {mode === 'channel' ? (
        <PermissionsSection />
      ) : isLoading ? (
        <Loading label={t('memberChannelAccessView.loadingMembers')} />
      ) : (
        <div className="grid gap-4 md:grid-cols-[minmax(240px,320px)_1fr]">
          <div className="rounded-lg border">
            <div className="border-b p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('memberChannelAccessView.searchMember')} className="pl-8" />
              </div>
            </div>
            <ul className="max-h-[32rem] overflow-y-auto p-1">
              {members.map((m) => {
                const active = selected?.id === m.id
                const explicit = needsExplicitAccess(m)
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(m.id)}
                      className={cn('flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left', active ? 'bg-primary/10' : 'hover:bg-muted')}
                    >
                      <Avatar className="h-8 w-8">
                        {avatarSrc(m.avatarUrl) && <AvatarImage src={avatarSrc(m.avatarUrl)} />}
                        <AvatarFallback>{initials(m.fullName || m.email)}</AvatarFallback>
                      </Avatar>
                      <span className="min-w-0 flex-1">
                        <span className={cn('block truncate text-sm', active && 'font-medium text-primary')}>{m.fullName || m.email}</span>
                        <span className="block truncate text-xs text-muted-foreground">{m.roleName || ROLE_LABELS[m.role]}</span>
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {explicit ? t('memberChannelAccessView.channelCount', { count: m.accounts.length }) : t('memberChannelAccessView.all')}
                      </span>
                    </button>
                  </li>
                )
              })}
              {!members.length && <li className="p-4 text-center text-sm text-muted-foreground">{t('memberChannelAccessView.noMembersFound')}</li>}
            </ul>
          </div>

          <div className="min-w-0 rounded-lg border p-4">
            {selected ? (
              <>
                <div className="mb-3">
                  <p className="font-medium">{selected.fullName || selected.email}</p>
                  <p className="text-xs text-muted-foreground">{selected.email} · {selected.roleName || ROLE_LABELS[selected.role]}</p>
                </div>
                <MemberAccountsPanel member={selected} />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('memberChannelAccessView.selectMemberOnLeft')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
